import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["requiredField", "trackField", "submitStep", "submitButton", "autosaveInput", "autosaveForm"]

  static values = {
    requiredOtherSectionsBeforeSubmit: Boolean,
    changed: Boolean
  }

  connect() {
    requestAnimationFrame(() => {
      this.storeInitialValues()

      this.requiredFieldTargets.forEach((field) => {
        this.toggleFieldError(field)
      })

      this.validateForm()
    })
  }

  storeInitialValues() {
  this.trackFieldTargets.forEach((field) => {
      if (field.type === "radio" || field.type === "checkbox") {
        field.dataset.initialChecked = String(field.checked)
        field.dataset.lastAutosavedChecked = String(field.checked)
      } else {
        const value = field.value || ""
        field.dataset.initialValue = value
        field.dataset.lastAutosavedValue = value
      }
    })
  }

  validateField(event) {
    const field = event.target

    this.toggleFieldError(field)
    this.validateForm()
    this.saveField(field)
  }

  clearError(event) {
    const field = event.target

    this.toggleFieldError(field)
    this.validateForm()
  }

  saveField(field) {

    if (field.type === "radio") {
      if (!field.checked) return

      const lastAutosavedChecked = field.dataset.lastAutosavedChecked
      const currentChecked = String(field.checked)

      if (currentChecked === lastAutosavedChecked) return

      if (this.hasAutosaveInputTarget) {
        this.autosaveInputTarget.value = "true"
      }

      const radioGroup = this.trackFieldTargets.filter((target) => {
        return target.type === "radio" && target.name === field.name
      })

      radioGroup.forEach((radio) => {
        radio.dataset.lastAutosavedChecked = String(radio.checked)
      })

      this.autosaveFormTarget.requestSubmit()
      return
    }

    if (field.type === "checkbox") {
      const currentChecked = String(field.checked)
      const lastAutosavedChecked = field.dataset.lastAutosavedChecked

      if (currentChecked === lastAutosavedChecked) return

      if (this.hasAutosaveInputTarget) {
        this.autosaveInputTarget.value = "true"
      }

      field.dataset.lastAutosavedChecked = currentChecked
      this.autosaveFormTarget.requestSubmit()
      return
    }

    const currentValue = this.normalizeValue(field.value)
    const initialValue = this.normalizeValue(field.dataset.initialValue)
    const lastAutosavedValue = this.normalizeValue(field.dataset.lastAutosavedValue)

    const hasChangedFromInitial = currentValue !== initialValue
    const hasChangedSinceLastAutosave = currentValue !== lastAutosavedValue
    const isFilled = currentValue !== ""
    const isValid = this.isFieldValid(field)

    if (!hasChangedFromInitial || !hasChangedSinceLastAutosave || !isFilled || !isValid) return

    if (this.hasAutosaveInputTarget) {
      this.autosaveInputTarget.value = "true"
    }

    field.dataset.lastAutosavedValue = currentValue
    this.autosaveFormTarget.requestSubmit()
  }

  hasAnyFieldChanged() {
    return this.trackFieldTargets.some((field, index) => {
      if (field.type === "radio" || field.type === "checkbox") {
        const currentChecked = String(field.checked)
        const initialChecked = String(field.dataset.initialChecked)
        const changed = currentChecked !== initialChecked

        return changed
      }

      const currentValue = (field.value || "").trim()
      const initialValue = (field.dataset.initialValue || "").trim()
      const changed = currentValue !== initialValue


      return changed
    })
  }

  validateForm() {
    const allFilled = this.requiredFieldTargets.every((field) => {
      return this.toggleFieldError(field)
    })

    const hasEmptyField = !allFilled
    const hasChangedInCurrentStep = this.hasAnyFieldChanged()

    if (this.hasSubmitStepTarget) {
      this.submitStepTarget.classList.toggle("disabled", hasEmptyField)
      this.submitStepTarget.classList.toggle("pointer-events-none", hasEmptyField)
      this.submitStepTarget.setAttribute("aria-disabled", hasEmptyField ? "true" : "false")
    }

    const hasChangedInEntireForm = this.changedValue || hasChangedInCurrentStep

    
    const canSubmitFinalForm =
      allFilled &&
      this.requiredOtherSectionsBeforeSubmitValue &&
      hasChangedInEntireForm 

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !canSubmitFinalForm
    }
  }

  preventNavigationIfInvalid(event) {
    if (!this.hasSubmitStepTarget) return

    if (this.submitStepTarget.classList.contains("disabled")) {
      event.preventDefault()
    }
  }

  disableSubmit() {
    this.submitButtonTarget.disabled = true
  }

  filterDigits(event) {
    event.target.value = event.target.value.replace(/\D/g, "")
  }

  filterPhone(event) {
    let value = event.target.value

    value = value.replace(/[^0-9+\- ]/g, "")  
        
    const hasLeadingPlus = value.startsWith("+")
    value = value.replace(/\+/g, "")

    if (hasLeadingPlus) {
      value = "+" + value
    }

    event.target.value = value
  }

  toggleFieldError(field) {
    const isValid = this.isFieldValid(field)

    if (field.type === "radio") {
      const group = this.requiredFieldTargets.filter((target) => {
        return target.type === "radio" && target.name === field.name
      })

      group.forEach((radio) => {
        radio.classList.toggle("error", !isValid)
      })

      return isValid
    }

    field.classList.toggle("error", !isValid)
    return isValid
  }

  normalizeValue(value) {
    return (value || "").trim()
  }

  isFieldValid(field) {
    if (field.type === "radio") {
      const group = this.requiredFieldTargets.filter((target) => {
        return target.type === "radio" && target.name === field.name
      })

      return group.some((radio) => radio.checked)
    }

    if (field.type === "checkbox") {
      return field.checked
    }

    if (field.tagName === "SELECT") {
      return !!field.value
    }

    if (field.dataset.validateEmail !== undefined) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(field.value)
    }

    return (field.value || "").trim() !== ""
  }
}